import React, { useEffect, useState } from "react";
import api from "../auth/api";
import PhonePreview from "./MessagePhonePreview";

export default function MessageTemplateEditor({ template, onClose }) {
  const [form, setForm] = useState({
    command: "",
    title: "",
    content: "",
    platform: "both",
    type: "text",
    status: "draft",
    imageUrl: null,
  });

  const [channels, setChannels] = useState([]);
  const [assigned, setAssigned] = useState({});
  const [uploading, setUploading] = useState(false);

  /* -----------------------------
      HANDLE CHANGE GLOBAL
  ------------------------------*/
  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  /* -----------------------------
      CARGAR CANALES
  ------------------------------*/
  const loadChannels = async () => {
    const res = await api.get("/channels");
    setChannels(res.data.rows || []);
  };

  /* -----------------------------
      CARGAR ASIGNACIONES
  ------------------------------*/
  const loadAssigned = async (templateId) => {
    const channelsRes = await api.get("/channels");
    const list = channelsRes.data.rows || [];

    let map = {};

    for (const ch of list) {
      const res = await api.get(`/api/messages/channels/${ch.id}/templates`);
      const tpl = res.data.rows.find((x) => x.id === templateId);
      map[ch.id] = tpl ? tpl.enabled == 1 : false;
    }

    setAssigned(map);
  };

  /* -----------------------------
      CARGAR FORM EN EDICIÓN
  ------------------------------*/
  useEffect(() => {
    loadChannels();

    if (template) {
      setForm({
        command: template.command || "",
        title: template.title || "",
        content: template.content || "",
        platform: template.platform || "both",
        type: template.type || "text",
        status: template.status || "draft",
        imageUrl: template.image_url || null,
      });

      loadAssigned(template.id);
    }
  }, []);

  /* -----------------------------
      TOGGLE CANAL
  ------------------------------*/
  const toggleChannel = (chId) => {
    setAssigned((prev) => ({
      ...prev,
      [chId]: !prev[chId],
    }));
  };

  /* -----------------------------
      SUBIR IMAGEN
  ------------------------------*/
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Vista previa instantánea
    const reader = new FileReader();
    reader.onload = () => {
      setForm((prev) => ({ ...prev, imageUrl: reader.result }));
    };
    reader.readAsDataURL(file);

    setUploading(true);

    const fd = new FormData();
    fd.append("image", file);

    try {
      const res = await api.post(
        "/api/messages/templates/upload-image",
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      setForm((prev) => ({
        ...prev,
        imageUrl: res.data.imageUrl,
      }));
    } catch (err) {
      console.error(err);
      alert("Error subiendo imagen");
    }

    setUploading(false);
  };

  /* -----------------------------
      GUARDAR
  ------------------------------*/
  const handleSave = async () => {
    if (!form.command || !form.content) {
      alert("command y contenido son obligatorios");
      return;
    }

    const payload = { ...form };
    let tplId = template?.id;

    // Crear
    if (!tplId) {
      const res = await api.post("/api/messages/templates", payload);
      tplId = res.data.id;
    } else {
      // Editar
      await api.patch(`/api/messages/templates/${tplId}`, payload);
    }

    // Guardar asignación channel_template
    for (const ch of channels) {
      await api.post(`/api/messages/channels/${ch.id}/templates/${tplId}`, {
        enabled: assigned[ch.id] ? 1 : 0,
      });
    }

    onClose();
  };

  return (
    <div className="h-full flex flex-col bg-gray-900 text-white">
      {/* HEADER */}
      <div className="flex justify-between items-center px-4 py-3 bg-gray-800 border-b border-gray-700">
        <strong className="text-lg">
          {template ? "Editar Plantilla" : "Nueva Plantilla"}
        </strong>

        <button
          onClick={onClose}
          className="text-gray-300 hover:text-white text-xl px-2"
        >
          ✕
        </button>
      </div>

      {/* BODY */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="flex flex-col gap-4">
          {/* COMMAND */}
          <div>
            <label className="text-sm text-gray-300">Command:</label>
            <input
              value={form.command}
              onChange={(e) => handleChange("command", e.target.value)}
              className="w-full bg-gray-800 text-white p-2 rounded border border-gray-700"
            />
          </div>

          {/* TITLE */}
          <div>
            <label className="text-sm text-gray-300">Título:</label>
            <input
              value={form.title}
              onChange={(e) => handleChange("title", e.target.value)}
              className="w-full bg-gray-800 text-white p-2 rounded border border-gray-700"
            />
          </div>

          {/* CONTENT */}
          <div>
            <label className="text-sm text-gray-300">Contenido:</label>
            <textarea
              value={form.content}
              onChange={(e) => handleChange("content", e.target.value)}
              rows={4}
              className="w-full bg-gray-800 text-white p-2 rounded border border-gray-700 resize-y"
            />
          </div>

          {/* PLATFORM */}
          <div>
            <label className="text-sm text-gray-300">Plataforma:</label>
            <select
              value={form.platform}
              onChange={(e) => handleChange("platform", e.target.value)}
              className="w-full bg-gray-800 text-white p-2 rounded border border-gray-700"
            >
              <option value="whatsapp">WhatsApp</option>
              <option value="telegram">Telegram</option>
              <option value="both">Ambas</option>
            </select>
          </div>

          {/* TYPE */}
          <div>
            <label className="text-sm text-gray-300">Tipo:</label>
            <select
              value={form.type}
              onChange={(e) => handleChange("type", e.target.value)}
              className="w-full bg-gray-800 text-white p-2 rounded border border-gray-700"
            >
              <option value="text">Texto</option>
              <option value="image">Imagen</option>
              <option value="button">Botón</option>
            </select>
          </div>

          {/* IMAGE UPLOAD */}
          {form.type === "image" && (
            <div>
              <label className="text-sm text-gray-300">Imagen:</label>

              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="mt-1"
              />

              {uploading && (
                <p className="text-yellow-400">Subiendo imagen...</p>
              )}

              {form.imageUrl && (
                <img
                  src={form.imageUrl}
                  alt="Preview"
                  className="mt-2 w-40 rounded border border-gray-700"
                />
              )}
            </div>
          )}

          {/* STATUS */}
          <div>
            <label className="text-sm text-gray-300">Estado:</label>
            <select
              value={form.status}
              onChange={(e) => handleChange("status", e.target.value)}
              className="w-full bg-gray-800 text-white p-2 rounded border border-gray-700"
            >
              <option value="draft">Borrador</option>
              <option value="published">Publicado</option>
            </select>
          </div>

          {/* CANALES */}
          <div className="mt-5">
            <h3 className="font-bold mb-2">Canales permitidos:</h3>

            {channels.map((ch) => (
              <label
                key={ch.id}
                className="flex items-center gap-2 text-sm mb-1 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={assigned[ch.id] || false}
                  onChange={() => toggleChannel(ch.id)}
                />
                {ch.name}{" "}
                <span className="text-gray-400">({ch.type})</span>
              </label>
            ))}
          </div>

          {/* SAVE BUTTON */}
          <button
            onClick={handleSave}
            className="w-full bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded mt-2"
          >
            Guardar
          </button>
        </div>

        {/* PHONE PREVIEW */}
        <div className="mt-6">
          <PhonePreview template={form} />
        </div>
      </div>
    </div>
  );
}
